<template>
  <Teleport to="body">
    <Transition name="compare-slide">
      <div
        v-if="compareModalOpen"
        class="compare-panel"
        :style="comparePanelStyle"
      >
        <DpplHeaderFooterContent class="compare-panel-body h-100 min-h-0 flex-grow-1">
          <template #header>
            <v-sheet
              class="top-bar d-flex"
              height="58px"
            >
              <div class="w-100 px-6 d-flex justify-space-between align-center">
                <span class="text-body-1">Compare Job Profiles</span>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  aria-label="Close"
                  @click="closeCompareModal"
                />
              </div>
            </v-sheet>
          </template>

          <div
            v-if="loading"
            class="w-100 flex-grow-1 d-flex justify-center align-center compare-loading"
          >
            <v-progress-circular
              :size="40"
              :width="3"
              color="primary"
              indeterminate
            />
          </div>

          <div
            v-else-if="loadError"
            class="w-100 flex-grow-1 pa-6"
          >
            <p class="text-body-2 text-medium-emphasis">
              {{ loadError }}
            </p>
          </div>

          <template v-else>
            <ComparePane
              :profile="oldProfile"
              :fields="isIdChangedScenario ? oldFieldsPlain : diffFields"
              :subtitle="oldProfileSubtitle"
              :is-current-version="oldVersionIsCurrentVersion"
              :is-diff-pane="!isIdChangedScenario"
              :title-html="isIdChangedScenario ? undefined : titleDiffHtml"
              :acknowledged-at="compareSignedAt"
              right-border
              aria-label="Previously acknowledged job profile version"
            />
            <ComparePane
              :profile="newProfile"
              :fields="newFieldsPlain"
              :subtitle="newProfileSubtitle"
              :is-current-version="newVersionIsCurrentVersion"
              :is-diff-pane="false"
              aria-label="Current job profile"
            />
          </template>
        </DpplHeaderFooterContent>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import { useAppBase } from '@/stores/app/base';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useJobProfileCompare } from '@/composables/useJobProfileCompare';
import ComparePane from '@/components/library/employeeAcknowledgements/ComparePane.vue';

const { appHeaderHeight } = storeToRefs(useAppBase());

const comparePanelStyle = computed(() => ({
  '--nav-offset': `${appHeaderHeight.value}px`,
}));

const {
  compareModalOpen,
  compareOldProfileUlid,
  compareOldVersionUlid,
  compareNewProfileUlid,
  compareNewVersionUlid,
  compareAlertType,
  compareSignedAt,
} = storeToRefs(useEmployeeAcknowledgementsStore());
const { closeCompareModal } = useEmployeeAcknowledgementsStore();

const isIdChangedScenario = computed(() => (
  compareAlertType.value === 'job_profile_id_changed'
));

const {
  loading,
  loadError,
  oldProfile,
  newProfile,
  titleDiffHtml,
  diffFields,
  newFieldsPlain,
  oldFieldsPlain,
  oldVersionIsCurrentVersion,
  newVersionIsCurrentVersion,
  oldProfileSubtitle,
  newProfileSubtitle,
} = useJobProfileCompare({
  active: compareModalOpen,
  oldProfileUlid: compareOldProfileUlid,
  oldVersionUlid: compareOldVersionUlid,
  newProfileUlid: compareNewProfileUlid,
  newVersionUlid: compareNewVersionUlid,
});
</script>

<style scoped lang="less">
.compare-panel {
  position: fixed;
  top: var(--nav-offset);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 7;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: @bg-default;
}

.compare-panel-body {
  min-height: 0;
}

.compare-slide-enter-active,
.compare-slide-leave-active {
  transition: transform 0.25s ease-out;
}

.compare-slide-enter-from,
.compare-slide-leave-to {
  transform: translateY(100%);
}

.top-bar {
  border-bottom: 1px solid @stroke-default;
}

.compare-loading {
  min-height: 0;
}
</style>
